/**
 * @fileoverview Media type detection utilities for template IDs.
 *               Shared helpers used across content workflow components.
 *               PATH: src/utils/mediaTypeUtils.js
 */

/**
 * All valid template IDs:
 *   fb_standard, fb_carousel, fb_reel, fb_story_image, fb_story_video
 *   ig_standard, ig_carousel, ig_reel, ig_story_image, ig_story_video
 *   li_standard, li_carousel, li_reel
 */

/**
 * Returns true if the template produces video content (Reel or Story Video).
 * @param {string} templateId - e.g. "ig_reel", "fb_story_video"
 */
export function isVideoTemplate(templateId) {
  if (!templateId) return false;
  return templateId.includes('reel') || templateId.includes('story_video');
}

/**
 * Returns true if the template uses 9:16 vertical aspect ratio.
 * All reels and stories are vertical.
 * @param {string} templateId
 */
export function isVerticalTemplate(templateId) {
  if (!templateId) return false;
  return templateId.includes('reel') || templateId.includes('story');
}

/**
 * Returns true if the content is ephemeral (disappears after 24h).
 * Only Stories are ephemeral.
 * @param {string} templateId
 */
export function isEphemeralTemplate(templateId) {
  if (!templateId) return false;
  return templateId.includes('story');
}

/**
 * Strips the platform prefix (fb_ or ig_) to get the base type.
 * e.g. "ig_story_image" -> "story_image", "fb_reel" -> "reel"
 * @param {string} templateId
 */
export function getBaseType(templateId) {
  if (!templateId) return '';
  return templateId.replace(/^(fb|ig|li)_/, '');
}

/**
 * Returns the display/publishing dimensions for a template.
 * Video generation uses 720x1280 (exact 9:16, LTX-2 compatible).
 * @param {string} templateId
 * @param {boolean} [forGeneration=false] - If true, returns ComfyUI generation dimensions
 * @returns {{width: number, height: number}}
 */
export function getMediaDimensions(templateId, forGeneration = false) {
  const base = getBaseType(templateId);
  const platform = templateId?.startsWith('ig_') ? 'instagram' : templateId?.startsWith('li_') ? 'linkedin' : 'facebook';

  switch (base) {
    case 'standard':
      return { width: 1080, height: 1350 };
    case 'carousel':
      return platform === 'instagram'
        ? { width: 1080, height: 1350 }
        : { width: 1024, height: 1024 };
    case 'reel':
      return forGeneration
        ? { width: 720, height: 1280 }
        : { width: 1080, height: 1920 };
    case 'story_image':
      return { width: 1080, height: 1920 };
    case 'story_video':
      return forGeneration
        ? { width: 720, height: 1280 }
        : { width: 1080, height: 1920 };
    default:
      return { width: 1080, height: 1350 };
  }
}
